"use client";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import {
  FileText,
  Phone,
  CreditCard,
  MessageCircle,
  Play,
  Shield,
  Heart,
  Target,
} from "lucide-react";

const InternshipSelectionProcess = ({ details }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  // console.log("det: ", details);
  const steps = [
    {
      icon: FileText,
      title: "Fill Application Form",
      description: "Complete our detailed application form with your details",
      duration: "5 minutes",
    },
    {
      icon: Phone,
      title: "Selection Call",
      description:
        "You'll receive a payment link from Charchit to make payment",
      duration: "10 minutes",
    },
    {
      icon: CreditCard,
      title: "Secure Your Spot",
      description: `Make payment of ${
        details?.price || 1000
      } within 24 hours to confirm your place in the cohort`,
      duration: "2 minutes",
    },
    {
      icon: MessageCircle,
      title: "Join WhatsApp Group",
      description:
        "Get added to our exclusive cohort group for immediate access to resources and community",
      duration: "Instant",
    },
  ];

  const promises = [
    {
      icon: Heart,
      title: "Complete Effort",
      description: "We'll put in 100% to help you succeed",
    },
    {
      icon: Shield,
      title: "Honest Feedback",
      description: "No sugar-coating, just what you need to hear",
    },
    {
      icon: Target,
      title: "Proven Guidance",
      description: "Methods that have worked for hundreds before you",
    },
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-aurora-primary/5 via-transparent to-aurora-accent/5"></div>
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-aurora-primary/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-aurora-accent/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="font-style-gradient">Selection Process</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Simple, transparent, and designed to find the right candidates
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-12 gap-8 max-w-7xl mx-auto">
          {/* Left Side - Selection Process */}
          <div className="lg:col-span-7">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-6 sm:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primaryTheme via-aurora-accent to-aurora-secondary" />

              {steps.map((step, index) => {
                const Icon = step.icon;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -50 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="relative flex items-start gap-4 sm:gap-8 mb-12 last:mb-0"
                  >
                    {/* Step number and icon */}
                    <div className="flex flex-col items-center space-y-2 flex-shrink-0">
                      <div className="relative">
                        {/* Glow effect */}
                        <div className="absolute inset-0 bg-purple-500/30 rounded-full blur-xl animate-pulse-glow" />

                        {/* Icon container */}
                        <div className="relative bg-card border-2 border-purple-500 rounded-full p-3 sm:p-4 shadow-glass">
                          <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />
                        </div>
                      </div>

                      <div className="text-xs sm:text-sm font-medium text-purple-500 bg-purple-500/10 px-2 sm:px-3 py-1 rounded-full">
                        Step {index + 1}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 bg-glass-gradient backdrop-blur-glass border border-primaryTheme/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 group hover:border-primaryTheme/40 transition-all duration-300">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-lg sm:text-xl text-left font-semibold text-foreground group-hover:text-primaryTheme transition-colors">
                          {step.title}
                        </h3>
                        <span className="hidden md:inline text-sm text-purple-500 bg-purple-500/10 px-3 py-1 rounded-full font-medium">
                          {step.duration}
                        </span>
                      </div>

                      <p className="text-sm sm:text-[15px] text-muted-foreground text-left leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Right Side - Disclaimer */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="sticky top-8"
            >
              <div className="bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-xl border border-primaryTheme/30 rounded-3xl p-4 md:p-8 shadow-2xl hover:border-primaryTheme/50 transition-all duration-500 hover:shadow-primaryTheme/10">
                <div className="space-y-8">
                  {/* Header */}
                  <div className="text-center space-y-4">
                    {/* <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primaryTheme/20 to-aurora-accent/20 mb-4">
                      <Shield className="h-8 w-8 text-primaryTheme" />
                    </div> */}
                    <h3 className="text-xl font-bold text-white">
                      <span className="relative inline-block">
                        <span className="relative z-10 px-2 py-1">
                          Transparency First
                        </span>
                        <span className="absolute inset-0 bg-yellow-400/50 transform rotate-[-1deg] rounded-sm"></span>
                      </span>
                    </h3>
                    <p className=" text-[16px] text-muted-foreground text-lg leading-relaxed">
                      We don't guarantee you an internship — but we give you{" "}
                      <span className="font-style-gradient">
                        everything to succeed.
                      </span>
                    </p>
                  </div>

                  {/* Quote */}
                  <div className="relative bg-gradient-to-r from-primaryTheme/10 via-aurora-accent/10 to-primaryTheme/10 border border-primaryTheme/30 rounded-2xl p-6 text-center overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primaryTheme/5 to-transparent" />
                    <div className="relative z-10">
                      <p className="text-[16px] text-foreground font-medium leading-relaxed">
                        "Even your college doesn't guarantee a job after taking
                        lakhs – and we're just charging ₹1000."
                      </p>
                    </div>
                  </div>

                  {/* What we promise */}
                  <div className="space-y-6">
                    <div className="text-center">
                      <h4 className="text-xl font-bold text-foreground mb-2">
                        Our Commitment to You
                      </h4>
                      <div className="w-16 h-1 bg-gradient-to-r from-primaryTheme to-aurora-accent rounded-full mx-auto" />
                    </div>

                    <div className="space-y-4">
                      {promises.map((promise, index) => {
                        const PromiseIcon = promise.icon;
                        return (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{
                              duration: 0.4,
                              delay: 0.6 + index * 0.1,
                            }}
                            className="group flex items-start gap-4 p-2 rounded-2xl bg-primaryTheme/5 hover:border-purple-500/40 hover:from-purple-500/10 hover:to-purple-400/10 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10"
                          >
                            <div className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-400/20 group-hover:from-purple-500/30 group-hover:to-purple-400/30 transition-all duration-300">
                              <PromiseIcon className="h-5 w-5 text-purple-500" />
                            </div>
                            <div className="flex-1">
                              <h5 className="font-bold text-foreground mb-2 text-[16px] group-hover:text-purple-500 transition-colors">
                                {promise.title}
                              </h5>
                              <p className="text-muted-foreground text-[14px] leading-relaxed">
                                {promise.description}
                              </p>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom note */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-purple-500/10 border border-purple-500/20 backdrop-blur-sm hover:bg-purple-500/15 hover:border-purple-500/30 transition-all duration-300">
            <span className="text-purple-500 font-medium">
              ✨ Cohort Strength: Handpicking 10 Students
            </span>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default InternshipSelectionProcess;
